import type { TipDefinition } from './types';

/**
 * Every console tip id lives here. Copy lives in `locales/en/tips.json` under the
 * same key. Pages place tips by id only — never inline tip strings.
 */
export const tipCatalog = {
  // Auth
  'auth.password': { mode: 'inline' },
  'auth.phone': { mode: 'inline' },
  'auth.totp': { mode: 'inline' },

  // Account
  'account.locale': { mode: 'inline' },
  'account.timezone': { mode: 'inline' },

  // Dashboard / search / reports
  'dashboard.members.active': { mode: 'label' },
  'dashboard.giving.month': { mode: 'label' },
  'dashboard.attendance.recent': { mode: 'label' },
  'dashboard.messages.delivered': { mode: 'label' },
  'dashboard.messages.campaigns': { mode: 'label' },
  'dashboard.messages.failures': { mode: 'label' },
  'dashboard.updatedAt': { mode: 'label' },
  'search.scope': { mode: 'icon' },
  'reports.dateRange': { mode: 'inline' },

  // Church settings
  'settings.church.timezone': { mode: 'inline' },
  'settings.church.locale': { mode: 'inline' },
  'settings.church.baseCurrency': { mode: 'inline' },
  'settings.venues.purpose': { mode: 'icon' },
  'settings.staff.roles': { mode: 'inline' },
  'settings.modules.enable': { mode: 'inline' },
  'settings.audit.scope': { mode: 'icon' },
  'settings.audit.financial': { mode: 'icon' },
  'settings.campusName': { mode: 'inline' },

  // Security / devices / billing
  'security.requireTotp': { mode: 'inline' },
  'security.totpCode': { mode: 'inline' },
  'security.devices.trusted': { mode: 'inline' },
  'security.devices.revoke': { mode: 'inline' },
  'security.passwordless': { mode: 'icon' },
  'billing.seats': { mode: 'inline' },
  'billing.storage': { mode: 'inline' },
  'billing.readOnly': { mode: 'inline' },

  // Members
  'members.directory.purpose': { mode: 'icon' },
  'members.types.filter': { mode: 'icon' },
  'members.columns.type': { mode: 'icon' },
  'members.insights.active': { mode: 'label' },
  'members.insights.newInPeriod': { mode: 'label' },
  'members.insights.firstTimers': { mode: 'label' },
  'members.insights.openFollowUps': { mode: 'label' },
  'members.firstTimerPhone': { mode: 'inline' },
  'members.isDistantMember': { mode: 'inline' },
  'members.hasDisability': { mode: 'inline' },
  'members.status.inactivate': { mode: 'inline' },
  'members.status.transfer': { mode: 'inline' },
  'members.status.deceased': { mode: 'inline' },
  'members.status.restore': { mode: 'inline' },
  'members.household.relationship': { mode: 'inline' },
  'members.giving.fund': { mode: 'inline' },
  'members.departments.purpose': { mode: 'icon' },
  'members.council.purpose': { mode: 'icon' },
  'members.ministers.purpose': { mode: 'icon' },
  'members.ministers.currentlyServing': { mode: 'inline' },
  'members.ministers.active': { mode: 'inline' },
  'members.leadership.term': { mode: 'inline' },
  'members.followUps.purpose': { mode: 'icon' },
  'members.families.purpose': { mode: 'icon' },

  // Groups
  'groups.smartVsStatic': { mode: 'inline' },
  'groups.leadershipScope': { mode: 'inline' },
  'groups.term': { mode: 'inline' },

  // Attendance
  'attendance.sessionsVsKiosks': { mode: 'icon' },
  'attendance.kiosk.purpose': { mode: 'inline' },
  'attendance.guestCheckIn': { mode: 'inline' },

  // Programs / calendar / services
  'programs.churchTimezone': { mode: 'inline' },
  'programs.occurrenceVsProgram': { mode: 'icon' },
  'programs.calendar.day': { mode: 'inline' },
  'programs.nextDates': { mode: 'inline' },
  'programs.endsOn': { mode: 'inline' },
  'programs.specificDays': { mode: 'inline' },
  'programs.descriptionField': { mode: 'inline' },
  'services.descriptionField': { mode: 'inline' },
  'programs.joinOnline': { mode: 'inline' },
  'programs.joinCode': { mode: 'inline' },
  'programs.dayDetails.dates': { mode: 'inline' },

  // Giving
  'giving.tithe.fund': { mode: 'inline' },
  'giving.tithe.privacy': { mode: 'inline' },
  'giving.serviceOfferings.batch': { mode: 'inline' },
  'giving.programOfferings.batch': { mode: 'inline' },
  'giving.funds.purpose': { mode: 'icon' },
  'giving.funds.createInline': { mode: 'inline' },
  'giving.funds.code': { mode: 'inline' },
  'giving.funds.currency': { mode: 'inline' },
  'giving.funds.privacy': { mode: 'inline' },
  'giving.funds.typeName': { mode: 'inline' },
  'giving.funds.typeCreate': { mode: 'inline' },
  'giving.funds.balance': { mode: 'icon' },
  'giving.funds.active': { mode: 'inline' },
  'giving.funds.acceptsPledges': { mode: 'inline' },
  'giving.funds.availableForPrograms': { mode: 'inline' },
  'giving.funds.insights.given': { mode: 'label' },
  'giving.periods.close': { mode: 'inline' },
  'giving.periods.reopen': { mode: 'inline' },
  'giving.contribution.void': { mode: 'inline' },
  'giving.contribution.closedPeriod': { mode: 'inline' },
  'giving.giverIdentity': { mode: 'inline' },
  'giving.baseCurrency': { mode: 'icon' },

  // Projects / pledges
  'projects.pledgeVsGift': { mode: 'icon' },
  'projects.redeem': { mode: 'inline' },
  'projects.recordGift': { mode: 'inline' },
  'projects.fundingProgress': { mode: 'label' },

  // Spending
  'spending.expensesVsBudgets': { mode: 'icon' },
  'spending.periodLock': { mode: 'inline' },
  'spending.categoryCreateInline': { mode: 'inline' },
  'spending.payeeCreateInline': { mode: 'inline' },

  // Payments / Hubtel
  'payments.credentialsWriteOnly': { mode: 'inline' },
  'payments.testVsLive': { mode: 'inline' },
  'payments.ipAllowlist': { mode: 'inline' },
  'payments.elevatedSession': { mode: 'inline' },
  'payments.maskedKey': { mode: 'inline' },
  'hubtel.smsBalance': { mode: 'inline' },
  'hubtel.walletBalance': { mode: 'inline' },
  'hubtel.prepaidDepositId': { mode: 'inline' },
  'hubtel.liveBalance': { mode: 'inline' },

  // Messaging
  'messaging.smsLength': { mode: 'inline' },
  'messaging.scheduleTimezone': { mode: 'inline' },
  'messaging.templateCreateInline': { mode: 'inline' },
  'messaging.mergeFields': { mode: 'inline' },
  'messaging.automationSendTime': { mode: 'inline' },
  'messaging.listContactFilters': { mode: 'inline' },

  // Content
  'content.sermon.service': { mode: 'inline' },
  'content.sermon.serviceDate': { mode: 'inline' },
  'content.sermon.verses': { mode: 'inline' },
  'content.sermon.artwork': { mode: 'inline' },
  'content.sermon.audio': { mode: 'inline' },
  'content.sermon.downloadable': { mode: 'inline' },
  'content.sermon.when': { mode: 'inline' },
  'content.devotional.artwork': { mode: 'inline' },
  'content.devotional.audio': { mode: 'inline' },
  'content.devotional.notify': { mode: 'inline' },
  'content.devotional.schedule': { mode: 'inline' },
  'content.announcement.when': { mode: 'inline' },
  'content.announcement.expiresAt': { mode: 'inline' },
  'content.announcement.pinned': { mode: 'inline' },
  'content.video.thumbnail': { mode: 'inline' },
  'content.video.when': { mode: 'inline' },
  'content.video.live': { mode: 'inline' },
  'content.scheduleTimezone': { mode: 'icon' },

  // Prayer
  'prayer.whoCanRead': { mode: 'inline' },
  'prayer.anonymous': { mode: 'inline' },
  'prayer.openAudit': { mode: 'inline' },

  // Evangelism / community / branches
  'evangelism.purpose': { mode: 'icon' },
  'community.purpose': { mode: 'icon' },
  'branches.consolidated': { mode: 'inline' },
  'branches.purpose': { mode: 'icon' },

  // Gallery demos
  'gallery.name': { mode: 'inline' },
  'gallery.givingStat': { mode: 'label' },
  'gallery.iconDemo': { mode: 'icon' },
} as const satisfies Record<string, TipDefinition>;

export type TipId = keyof typeof tipCatalog;

export const tipIds = Object.keys(tipCatalog) as TipId[];

export function getTipDefinition(tipId: TipId): TipDefinition {
  return tipCatalog[tipId];
}
